import axios from "axios";
import { clearSession, getSession } from "@/lib/auth";

// In Cloud Run monorepo deployment, frontend and backend are same-origin.
const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_BASE = backendBase ? `${backendBase}/api` : "/api";

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 180000,
});

// FastAPI's `detail` is usually a string, but on a 422 (request validation
// failure) it's an array of {type, loc, msg, ...} objects instead — passing
// that straight into toast()/JSX crashes React ("Objects are not valid as a
// React child"). Always reduce to a plain string before rendering.
export const apiErrorMessage = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join("; ");
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return e?.message || "Something went wrong";
};

/** Trigger a file download from a Blob (works better on mobile than raw data URIs). */
export function downloadBlobFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Download a protected API file with the session Bearer token (window.open cannot). */
export async function downloadAuthenticatedFile(path, fallbackFilename) {
  const response = await api.get(path, { responseType: "blob" });
  const disposition = response.headers["content-disposition"] || "";
  const match = /filename="?([^";\n]+)"?/i.exec(disposition);
  const filename = match?.[1] || fallbackFilename;
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  downloadBlobFile(blob, filename);
}

/** Download a PNG from a data URI — converts to blob first for mobile browser support. */
export async function downloadDataUriFile(dataUri, filename) {
  const response = await fetch(dataUri);
  const blob = await response.blob();
  downloadBlobFile(blob, filename);
}

export const wsUrl = (path) => {
  if (backendBase) {
    const u = new URL(backendBase);
    const proto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${u.host}/api${path}`;
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api${path}`;
};

api.interceptors.request.use((config) => {
  const token = getSession()?.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const path = error.config?.url || "";
    if (error.response?.status === 401 && !path.includes("/auth/login")) {
      clearSession();
      const p = window.location.pathname;
      if (!p.startsWith("/login") && !p.startsWith("/announcer") && !p.startsWith("/scan") && !p.startsWith("/pass") && !p.startsWith("/convocation")) {
        const next = encodeURIComponent(p + window.location.search);
        window.location.href = `/login?next=${next}`;
      }
    }
    return Promise.reject(error);
  },
);
